import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useAuth } from './AuthContext'
import type { AppNotification } from '../types/notifications'
import {
  fetchNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from '../services/notifications'

interface NotificationContextValue {
  unreadCount: number
  recent: AppNotification[]
  loading: boolean
  refresh: () => Promise<void>
  markRead: (id: number) => Promise<void>
  markAllRead: () => Promise<void>
}

const NotificationContext = createContext<NotificationContextValue | undefined>(
  undefined
)

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user } = useAuth()
  const [unreadCount, setUnreadCount] = useState(0)
  const [recent, setRecent] = useState<AppNotification[]>([])
  const [loading, setLoading] = useState(false)

  const refresh = useCallback(async () => {
    if (!user) {
      setUnreadCount(0)
      setRecent([])
      return
    }
    setLoading(true)
    try {
      const data = await fetchNotifications(8, 0, false)
      setRecent(data.items)
      setUnreadCount(data.unreadCount)
    } catch {
      setUnreadCount(0)
      setRecent([])
    } finally {
      setLoading(false)
    }
  }, [user])

  useEffect(() => {
    refresh()
  }, [refresh])

  const markRead = useCallback(
    async (id: number) => {
      await markNotificationRead(id)
      setRecent((prev) =>
        prev.map((n) =>
          n.id === id ? { ...n, readAt: new Date().toISOString() } : n
        )
      )
      setUnreadCount((c) => Math.max(0, c - 1))
    },
    []
  )

  const markAllRead = useCallback(async () => {
    await markAllNotificationsRead()
    setRecent((prev) =>
      prev.map((n) => ({ ...n, readAt: n.readAt ?? new Date().toISOString() }))
    )
    setUnreadCount(0)
  }, [])

  const value = useMemo(
    () => ({
      unreadCount,
      recent,
      loading,
      refresh,
      markRead,
      markAllRead,
    }),
    [unreadCount, recent, loading, refresh, markRead, markAllRead]
  )

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  )
}

export function useNotifications(): NotificationContextValue {
  const ctx = useContext(NotificationContext)
  if (!ctx) {
    throw new Error('useNotifications must be used inside <NotificationProvider>')
  }
  return ctx
}
